// Sugalarm — top-level layout chooser. Renders either the fixed 1024×600
// kiosk frame or a full-viewport web frame around the active screen.
//
// Both frames host the same routes; cycle 10 will fork screen layouts
// where useful. Both frames render the urgent overlay above everything
// else when an urgent alert is live.

import { type CSSProperties, type ReactNode, useEffect, useState } from "react";

import type { FrameVariant } from "../../hooks/useFrameVariant";
import type { GlucoseSocketState } from "../../hooks/useGlucoseSocket";
import { useThresholds } from "../../hooks/useThresholds";
import {
  isAndroidTabletClassRuntime,
  isIPadClassRuntime,
  kioskStageHeight,
  KIOSK_STAGE_HEIGHT_DEFAULT,
  KIOSK_STAGE_WIDTH_DEFAULT,
} from "../../lib/deviceClass";
import type { TTSState } from "../../hooks/useTTS";
import { useVolumeAgent } from "../../hooks/useVolumeAgent";
import { useKioskVolume } from "../../hooks/useKioskVolume";
import type { Member, User } from "../../lib/types";
import { fmtAge, trendInfo, zoneOf } from "../../lib/glucose";
import {
  liftFieldAboveKeyboard,
  releaseKeyboardLift,
} from "../../lib/kioskKeyboardScroll";
import { BroadcastBanner } from "../BroadcastBanner";
import { NoDataAlarmBanner } from "./NoDataAlarmBanner";
import { isTextEntry } from "../../lib/kioskKeyboard";
import { OnScreenKeyboard } from "../ui/OnScreenKeyboard";
import { SpeakingBanner } from "../voice/SpeakingBanner";
import { ThemeBackground } from "../ThemeBackground";
import { WakeBanner } from "../voice/WakeBanner";
import { EdgeRail } from "./EdgeRail";
import { FooterTabs } from "./FooterTabs";
import { KioskHeader } from "./KioskHeader";
import { MobileFrame } from "./MobileFrame";
import { WebFrame } from "../web/WebFrame";
import { selectCurrentGlucoseFor } from "../../lib/currentGlucose";

interface Props {
  variant: FrameVariant;
  user: User | null;
  socket: GlucoseSocketState;
  tts: TTSState;
  now: number;
  /** Display units, threaded from the SettingsProvider in App.tsx —
   *  consumed by KioskHeader's hero readout and MobileFrame's compact
   *  collapsed-header readout. */
  units: "mg/dL" | "mmol/L";
  /** Active member's display name — used by the manual Speak Status
   *  button so the read-out greets the right person, and by the
   *  mobile frame's compact header (cycle 25). */
  memberName?: string | null;
  /** Cycle 105: active member's avatar id (null → Sugalarm shield),
   *  threaded into the kiosk/web header + the mobile header. The
   *  relationship subtitle was removed from the header this cycle. */
  memberAvatarId?: string | null;
  /** When false, the chrome (header / nav / overlay) is hidden — used
   *  for the kiosk-pairing screen and the login screen. */
  showChrome?: boolean;
  /** Cycle 222: episode-level urgent — the frame-level SpeakingBanner stays
   *  safety-quiet (does not render) while an urgent alarm is active. */
  urgentActive?: boolean;
  /** Cycle 394: a bottom alarm strip is (or may be) mounted on the kiosk Now
   *  screen, so the SpeakingBanner lifts clear of it.
   *
   *  ⚠ DELIBERATELY A SUPERSET of the strips' own mount gate, and the
   *  asymmetry is the point: lifting when no strip is present costs 40px of
   *  empty space, while failing to lift covers a live alarm's Snooze/Stop
   *  (the cycle-81 floor). When in doubt, lift. Do NOT "tighten" this into a
   *  third copy of the banner mount condition — that is the drift hazard the
   *  clock-mode reservation already carries one of. */
  alarmStripActive?: boolean;
  /** Cycle 402: the bedside clock FACE is showing (`clockMode.active` AND
   *  the route is `/now`). App.tsx owns that one expression and feeds it
   *  BOTH here and into `showChrome`, so the two can never disagree.
   *
   *  ⚠ IT IS WHAT LETS THE NO-DATA BANNER SURVIVE THE CHROME SUPPRESSION.
   *  `showChrome` goes false for THREE unrelated reasons — `/login`, the
   *  Android pairing takeover, and the clock face — and only the third
   *  should still show a dark-feed alarm. There is no member context on
   *  login or pairing, so the banner must stay suppressed there. */
  clockFaceActive?: boolean;
  predictionPill?: string | null;
  /** Cycle 100 — epoch-ms of the most recent kiosk wake event (from the
   *  voice agent's SSE stream). Drives the brief "Listening" WakeBanner
   *  pulse on the kiosk. null / undefined on web + mobile (no voice). */
  voiceWakeTs?: number | null;
  /** Cycle 383 — a hands-free voice command session is capturing or
   *  thinking. Drives the SAME `WakeBanner` the cycle-100 wake pulse uses
   *  (no new visual component), held up for the life of the session
   *  instead of a fixed 1.6 s. Android kiosk + Android phone only; false /
   *  undefined everywhere else, which restores the cycle-100 behaviour
   *  exactly. */
  voiceListening?: boolean;
  /** Cycle 170: web command-station status strip inputs — the accessible
   *  member set, the active/viewed member, and the switch handler. WEB
   *  FRAME ONLY; kiosk + mobile ignore them. */
  members?: Member[];
  activeMemberId?: string | null;
  onSelectMember?: (memberId: string) => void;
  /** Cycle 360.5: the kiosk in-app no-data alarm is sounding — mount the
   *  frame-level Silence banner (visible on every screen). KIOSK ONLY (the
   *  hook only ever sets this true on the kiosk variant). */
  noDataAlarmActive?: boolean;
  onSilenceNoData?: () => void;
  /** Cycle 453.2 (operator decision 3, option a): a takeover that paints
   *  OUTSIDE the stage is up (today: the reminder). While it is, the kiosk
   *  no-data banner draws in a stage-aligned layer above it, and the frame
   *  publishes the band it occupies as `--alarm-bottom-band` on the document
   *  root so the takeover keeps its own controls clear of it. */
  alarmTopLayer?: boolean;
  /** Cycle 452: the compact alarm control, when the shell has decided one is
   *  owed off `/now`. A flex item that takes its own height, so the route
   *  shrinks beside it rather than being covered. NOT behind the chrome gate:
   *  it must reach the pairing family. Null → every frame is byte-identical.
   *  Cycle 452.2 (operator decision 2026-09-25): it is Now's banner, where
   *  Now's banner is on each surface — on the KIOSK along the bottom of the
   *  stage, full width (the `.kioskAlarmBanner` slot inside `.kioskContent`,
   *  cycle 394's `.alarmRowBottom` treatment), on the Android pairing
   *  takeover too; on phones and web in the banner slot under the header. */
  alarmControl?: ReactNode;
  children: ReactNode;
}

// Cycle 53: the kiosk design is a pixel-fixed canvas. On a larger screen
// (an iPad in landscape — cycle 52 routes that to the kiosk variant) it
// would otherwise sit small and centred. Scale the whole canvas uniformly
// to the largest size that still fits, aspect preserved (contain — never
// crop a data UI). The real Pi viewport is exactly 1024×600 so this is
// exactly 1.0 and the transform is omitted → byte-identical Pi behaviour.
//
// Cycle 199: the design-box height is `kioskStageHeight()` — 600 on the Pi
// + iPad, 640 (16:10) on an Android-tablet-class runtime. The denominator
// uses the SAME helper that sets the `.kiosk` CSS height (set inline in
// the render below), so width and height bind to one scale and a 16:10
// tablet has zero letterbox. A1920×1200 tablet → min(1920/1024, 1200/640)
// = 1.875 on both axes.
interface KioskStage {
  /** Uniform `transform: scale()` applied to the `.kiosk` design box. */
  scale: number;
  /** The `.kiosk` design-box WIDTH in px (≥ 1024). */
  width: number;
}

function computeKioskStage(): KioskStage {
  if (typeof window === "undefined") {
    return { scale: 1, width: KIOSK_STAGE_WIDTH_DEFAULT };
  }
  const iw = window.innerWidth;
  const ih = window.innerHeight;
  const designHeight = kioskStageHeight();
  // Cycle 325: FLUID canvas width — widen-only. The pre-325 design box was a
  // fixed 1024×designHeight, so a device WIDER than that aspect (the non-16:10
  // A11 tablet, 1340×800) scaled to height and left ~28px letterbox bars on
  // each side. Now, when the viewport is wider than the design aspect, the
  // design-box WIDTH grows to the exact viewport aspect (`designHeight×iw/ih`)
  // so a UNIFORM scale fills BOTH axes → no letterbox, no distortion (rings
  // stay circular), no cropping. `Math.max(1024, …)` makes it WIDEN-ONLY: a
  // device at-or-narrower than the design aspect (Pi 1024×600, iPad 4:3, the
  // 16:10 A11+/A9+ tablets) clamps to exactly 1024 → the pre-325 fixed box, and
  // the `min()` below reduces to the old `min(iw/1024, ih/designHeight)` → the
  // scale + width are byte-identical. Only a wider-than-design device changes,
  // and only in the safe "more horizontal room" direction.
  const aspectWidth =
    ih > 0 ? designHeight * (iw / ih) : KIOSK_STAGE_WIDTH_DEFAULT;
  const width =
    Number.isFinite(aspectWidth) && aspectWidth > KIOSK_STAGE_WIDTH_DEFAULT
      ? aspectWidth
      : KIOSK_STAGE_WIDTH_DEFAULT;
  const s = Math.min(iw / width, ih / designHeight);
  return { scale: Number.isFinite(s) && s > 0 ? s : 1, width };
}

function useKioskStage(variant: FrameVariant): KioskStage {
  const [stage, setStage] = useState<KioskStage>(() =>
    variant === "kiosk"
      ? computeKioskStage()
      : { scale: 1, width: KIOSK_STAGE_WIDTH_DEFAULT },
  );
  useEffect(() => {
    if (variant !== "kiosk") {
      setStage({ scale: 1, width: KIOSK_STAGE_WIDTH_DEFAULT });
      return;
    }
    const apply = () => setStage(computeKioskStage());
    apply();
    window.addEventListener("resize", apply);
    window.addEventListener("orientationchange", apply);
    return () => {
      window.removeEventListener("resize", apply);
      window.removeEventListener("orientationchange", apply);
    };
  }, [variant]);
  return stage;
}

export function KioskFrame({
  variant,
  user,
  socket,
  tts,
  now,
  units,
  memberName = null,
  memberAvatarId = null,
  showChrome = true,
  urgentActive = false,
  alarmStripActive = false,
  clockFaceActive = false,
  predictionPill,
  voiceWakeTs = null,
  voiceListening = false,
  members = [],
  activeMemberId = null,
  onSelectMember = () => {},
  noDataAlarmActive = false,
  onSilenceNoData = () => {},
  alarmTopLayer = false,
  alarmControl = null,
  children,
}: Props) {
  // Cycle 22: kiosk-only system volume agent on 127.0.0.1:8765 (the Pi). The
  // hook short-circuits to `available: false` when variant !== "kiosk", so it
  // never makes network calls from the web frame.
  const volumeAgent = useVolumeAgent(variant);
  // Cycle 317: native volume on the ANDROID TABLET kiosk (STREAM_MUSIC via the
  // KioskVolume plugin). `available` is false unless the plugin is on the
  // bridge (the tablet APK), so the Pi keeps its loopback agent and every
  // other surface gets neither. Prefer the native source when present.
  const nativeVolume = useKioskVolume(variant);
  const volume = nativeVolume.available ? nativeVolume : volumeAgent;
  const { scale: kioskScale, width: kioskWidth } = useKioskStage(variant);
  const thresholds = useThresholds();

  // Cycle 452.2: the off-Now kiosk banner's own height, in LAYOUT px
  // (`offsetHeight` — the stage is painted through `transform: scale()`, so a
  // rect would be screen px: the cycle-400 trap). Handed to `.kioskContent`
  // as `--kiosk-alarm-banner-live` so the Android pairing takeover reserves
  // the band the banner actually occupies — 52 px, or more when a failed-ack
  // notice stacks under the row. Unmeasured (no ResizeObserver, or before
  // layout) falls back to the 52 px token.
  const [alarmBannerSlot, setAlarmBannerSlot] = useState<HTMLDivElement | null>(null);
  const [alarmBannerH, setAlarmBannerH] = useState<number | null>(null);
  useEffect(() => {
    if (!alarmBannerSlot) {
      setAlarmBannerH(null);
      return;
    }
    const read = () => setAlarmBannerH(alarmBannerSlot.offsetHeight || null);
    read();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(read);
    ro.observe(alarmBannerSlot);
    return () => ro.disconnect();
  }, [alarmBannerSlot]);

  // Cycle 199: on the Android-tablet kiosk the packaged OnScreenKeyboard
  // is suppressed (the native Android IME is used instead) — so reproduce
  // the cycle-14/45 scroll-into-view here: when a text/numeric field gains
  // focus, scroll it above the raised native IME. Gated strictly to the
  // Android-tablet kiosk; the Pi/iPad keep the OnScreenKeyboard (which owns
  // its own scroll) and never run this. The 250 ms delay lets the IME
  // begin to raise before we measure (the on-device-confirmed item).
  useEffect(() => {
    if (variant !== "kiosk" || !isAndroidTabletClassRuntime()) return;
    const onFocusIn = (e: FocusEvent) => {
      const t = e.target as HTMLElement | null;
      if (!t) return;
      const tag = t.tagName;
      if (tag !== "TEXTAREA" && tag !== "INPUT") return;
      const id = window.setTimeout(() => {
        // Cycle 277: a bare scrollIntoView no-ops when `.kioskContent` isn't a
        // scroll container. `liftFieldAboveKeyboard` makes it scrollable and
        // lifts the field clear of the raised IME; the native cycle-267 inset
        // already shrank the viewport, so inset 0 (no extra padding) is enough.
        liftFieldAboveKeyboard(t, 0);
      }, 250);
      // Clear if focus moves again before the timer fires; restore on blur.
      const cancel = () => {
        window.clearTimeout(id);
        releaseKeyboardLift();
      };
      t.addEventListener("blur", cancel, { once: true });
    };
    document.addEventListener("focusin", onFocusIn);
    return () => {
      document.removeEventListener("focusin", onFocusIn);
      releaseKeyboardLift();
    };
  }, [variant]);
  // Cycle 300: the kiosk nav is always the SIDE rail. The cycle-77
  // bottom-vs-side user setting + its Settings control were removed; the
  // stored `nav_position` value is ignored.
  const navPosition: "bottom" | "side" = "side";
  // Cycle 103: the curved cycle-79/86 header↔rail seam is gone — the
  // rail is now a detached pill, so there's no header↔rail join to round
  // and the header's bottom hairline runs full width on every screen.
  // ⚠ Cycle 435: the ONE selector decides the number every frame paints —
  // it samples `Date.now()` itself at this call, never the ticking `now`
  // prop, so a thaw is judged against the real clock on its first paint.
  const current = selectCurrentGlucoseFor(socket, activeMemberId);
  // Cycle 306: the header provider pill reflects the ACTIVE MEMBER's
  // connected CGM — member-scoped and fanned out via the `member_update`
  // frame on connect/disconnect (cycles 305/306). This replaces the stale
  // account-level `user.cgm_provider` (which disconnect never cleared and
  // the client never refreshed after a switch — so Libre lingered after
  // switching to Dexcom) and is the same source the Household cards use.
  const cgmProvider =
    socket.dashboardCards.find((c) => c.member_id === activeMemberId)
      ?.cgm_provider ?? null;
  // Cycle 433: the kiosk face follows the same display rule as the phone —
  // a number the client cannot prove is current renders as No Data. The
  // alarm surface, its controls and its tone are untouched.
  // Cycle 435: "stale" is exactly "the selector released nothing": the
  // server's verdict, the sample age in seconds and the per-member stream
  // confirmation are folded upstream. The mini value is WITHHELD, not
  // greyed. `shown` is the only value any child may paint.
  const shown = current.displayValue;
  const zone = shown ? zoneOf(shown.sgv, thresholds) : "unknown";
  const ageMin = current.ageMinutes ?? 0;
  const trendGlyph = shown ? trendInfo(shown.trend, shown.trendRate).glyph : "";

  // Cycle 229: the header Speak-status button was removed (replaced by the
  // tablet battery indicator), so its `onSpeakStatus`/`tts` props are gone.
  // TTS itself is unaffected — auto-announce, reminders, and the
  // SpeakingBanner orb still drive `tts` (used below + in WebFrame/MobileFrame).
  const header = showChrome ? (
    <KioskHeader
      current={shown}
      zone={zone}
      ageMin={ageMin}
      provider={cgmProvider}
      unackCount={socket.unackCount}
      prediction={predictionPill}
      now={now}
      h24={false}
      units={units}
      trendGlyph={trendGlyph}
      volume={volume}
      volumeHideMute={nativeVolume.available}
      memberName={memberName}
      memberAvatarId={memberAvatarId}
    />
  ) : null;

  const footer = showChrome ? <FooterTabs unackCount={socket.unackCount} /> : null;

  // Cycle 97: operator broadcast banner. Rendered in normal flow (it
  // reserves height + reflows content — never an overlay) between the
  // header and the content, on every variant. Renders null when there's
  // no active broadcast / it's dismissed / expired.
  const broadcastBanner = showChrome ? (
    <BroadcastBanner
      broadcast={socket.activeBroadcast}
      variant={variant}
      now={now}
    />
  ) : null;

  // Cycle 360.5: the kiosk in-app no-data alarm's Silence banner. Frame-level
  // (like the broadcast banner) so it reserves height + is visible on EVERY
  // screen — an un-silenceable alarm is a hazard, so the control must never
  // depend on which screen the kiosk sits on. Kiosk-only by construction
  // (`noDataAlarmActive` is only ever true on the kiosk variant).
  //
  // ⚠ CYCLE 453.1 (operator decision 2026-09-28): THE ONLY RULE IS "HIDDEN
  // WHEN SIGNED OUT", AND THE FRAME ENFORCES IT BY NOT EXISTING THERE. The
  // signed-out routes render outside this frame and outside the audio hook
  // (App.tsx), so no tone can sound and there is no banner to hide. Inside
  // the frame the banner shows whenever the tone can: no chrome term, no
  // route term. Cycle 402 had hidden it on `/login` and the `/pair` takeover
  // ("no member context") — true of the signed-OUT screens, but the tone also
  // sounds on the SIGNED-IN ones, where it then had no Silence anywhere
  // (reproduced 2026-09-28). Cycle 452.1's `noDataOnTakeover` exception is
  // gone with the rule it was an exception to.
  //
  // On a chrome-less surface (signed-in `/login`, the Android pairing
  // takeover) the banner sits flush on the stage's bottom edge — the
  // off-Now banner's placement there (452.2). On the clock face it is fixed
  // to the face's bottom edge (cycle 402).
  //
  // Cycle 453.2: while an out-of-stage takeover is up, the SAME banner draws
  // in the stage-aligned top layer instead (`topLayer`); see the prop doc.
  const noDataTopLayer =
    variant === "kiosk" && alarmTopLayer
      ? { scale: kioskScale, width: kioskWidth, height: kioskStageHeight() }
      : null;
  const noDataBanner = noDataAlarmActive ? (
    <NoDataAlarmBanner
      onSilence={onSilenceNoData}
      clockFaceActive={clockFaceActive}
      flush={!showChrome && !clockFaceActive}
      topLayer={noDataTopLayer}
    />
  ) : null;

  // Cycle 453.2: the band at the bottom of the VIEWPORT that a bottom alarm
  // banner occupies (the no-data banner, in the stage or lifted above a
  // takeover, and the off-Now banner), published as `--alarm-bottom-band`
  // on the document root. It is read ONLY by overlays that paint outside
  // the stage (the reminder takeover, the avatar nudge, the activation
  // prompt, the missed-reminder strip) to keep themselves clear of the
  // banner — those overlays live in screen px, so a screen-px rect is the
  // right measure here (unlike a design-px comparison inside the stage,
  // the cycle-400 trap). Kiosk only; removed when no banner is up.
  // Cycle 453.2 (operator decision 4): a text field has focus on the kiosk,
  // so the keyboard may be up. The bottom alarm banners dock under the
  // header meanwhile (`.kioskTextFocus`, tokens.css). A field-to-field move
  // fires focusout before focusin, so the loss is re-sampled a tick later
  // rather than bouncing the banner to the bottom and back.
  //
  // ⚠ A PRESS ON A DOCKED BANNER MUST NOT MOVE IT. Pressing Silence / Snooze
  // blurs the field on pointerdown; if the dock followed focus at once the
  // banner would jump to the bottom between pointerdown and pointerup and the
  // tap would land on whatever is now under the finger (measured in the
  // repro: the Silence tap missed). So while a press that started on a
  // banner is in progress the dock is held, and focus is re-read only after
  // the press — and its click — have finished.
  const [textFocus, setTextFocus] = useState(false);
  useEffect(() => {
    if (variant !== "kiosk" || typeof document === "undefined") {
      setTextFocus(false);
      return;
    }
    let held = false;
    const sync = () => {
      if (!held) setTextFocus(isTextEntry(document.activeElement));
    };
    let t: ReturnType<typeof setTimeout> | undefined;
    const later = () => {
      if (t !== undefined) clearTimeout(t);
      t = setTimeout(sync, 0);
    };
    const onDown = (e: Event) => {
      const el = e.target as Element | null;
      if (el?.closest?.("[data-alarm-bottom-band]")) held = true;
    };
    const onUp = () => {
      if (!held) return;
      // After the click that follows this pointerup has been dispatched.
      setTimeout(() => {
        held = false;
        sync();
      }, 0);
    };
    sync();
    document.addEventListener("focusin", sync);
    document.addEventListener("focusout", later);
    document.addEventListener("pointerdown", onDown, true);
    document.addEventListener("pointerup", onUp, true);
    document.addEventListener("pointercancel", onUp, true);
    return () => {
      if (t !== undefined) clearTimeout(t);
      document.removeEventListener("focusin", sync);
      document.removeEventListener("focusout", later);
      document.removeEventListener("pointerdown", onDown, true);
      document.removeEventListener("pointerup", onUp, true);
      document.removeEventListener("pointercancel", onUp, true);
    };
  }, [variant]);

  const noDataLifted = noDataTopLayer != null;
  const hasAlarmControl = alarmControl != null;
  useEffect(() => {
    if (variant !== "kiosk" || typeof document === "undefined") return;
    const root = document.documentElement;
    let raf = 0;
    const measure = () => {
      let band = 0;
      document
        .querySelectorAll<HTMLElement>("[data-alarm-bottom-band]")
        .forEach((el) => {
          // A banner docked under the header while a field has focus is not
          // at the bottom; it reserves no bottom band (astra, 453.2).
          if (el.closest(".kioskTextFocus")) return;
          const r = el.getBoundingClientRect();
          if (r.height > 0) band = Math.max(band, window.innerHeight - r.top);
        });
      if (band > 0) root.style.setProperty("--alarm-bottom-band", `${Math.ceil(band)}px`);
      else root.style.removeProperty("--alarm-bottom-band");
    };
    const schedule = () => {
      if (typeof requestAnimationFrame === "undefined") return measure();
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(measure);
    };
    schedule();
    window.addEventListener("resize", schedule);
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(schedule) : null;
    document
      .querySelectorAll<HTMLElement>("[data-alarm-bottom-band]")
      .forEach((el) => ro?.observe(el));
    return () => {
      if (typeof cancelAnimationFrame !== "undefined") cancelAnimationFrame(raf);
      window.removeEventListener("resize", schedule);
      ro?.disconnect();
      root.style.removeProperty("--alarm-bottom-band");
    };
  }, [
    variant,
    noDataAlarmActive,
    noDataLifted,
    hasAlarmControl,
    alarmBannerH,
    kioskScale,
    kioskWidth,
    showChrome,
    clockFaceActive,
    textFocus,
  ]);

  // Cycle 401: the no-data banner now shares the bottom edge with the Now
  // screen's alarm strip, so the cycle-394 SpeakingBanner collision applies to
  // it as well — and applies on EVERY screen, because this banner is
  // frame-level while `alarmStripActive` only ever describes the Now screen.
  //
  // ⚠ OR, NEVER AND, AND NEVER "TIGHTENED" INTO A SINGLE CONDITION. Lifting
  // with no strip present costs 40px of empty space; failing to lift paints a
  // voice caption over the Silence button of an un-silenceable alarm. Same
  // asymmetry the `alarmStripActive` prop doc already spells out.
  const bottomStripActive =
    alarmStripActive || Boolean(noDataBanner) || Boolean(alarmControl);
  // Cycle 452.2: the Low / High / Urgent banners and the off-Now banner are
  // 52 px (44 px buttons). Cycle 453.1: so is the no-data banner now, so every
  // bottom banner lifts the caption by the one 52 px row.

  // Cycle 452.2: the kiosk slot for the off-Now banner — last on screen,
  // first in the DOM, exactly like the no-data strip below it (cycle 401). On
  // a chrome-less surface (the Android pairing takeover) `.kioskContent` still
  // reserves the bottom-nav safe-area though no nav is drawn; the flush class
  // puts the banner on the stage's bottom edge instead, and the marker on
  // `.kioskContent` lets the takeover reserve that band.
  const kioskAlarmBanner = alarmControl ? (
    <div
      ref={setAlarmBannerSlot}
      data-alarm-bottom-band=""
      className={`kioskAlarmBanner${showChrome ? "" : " kioskAlarmBannerFlush"}`}
    >
      {alarmControl}
    </div>
  ) : null;
  const kioskContentStyle: CSSProperties | undefined =
    alarmControl && alarmBannerH != null
      ? ({ "--kiosk-alarm-banner-live": `${alarmBannerH}px` } as CSSProperties)
      : undefined;

  if (variant === "mobile") {
    // Cycle 267: a non-official tablet (iPad / non-kiosk Android tablet) was
    // demoted to the mobile variant by useFrameVariant; render it as a
    // CENTERED phone-width column over a neutral backdrop instead of
    // stretching the phone layout edge-to-edge. Phones never match the
    // tablet predicates, so they take the plain full-bleed MobileFrame.
    const tabletCentered =
      isIPadClassRuntime() || isAndroidTabletClassRuntime();
    const mobileFrame = (
      <MobileFrame
        user={user}
        socket={socket}
        now={now}
        units={units}
        ageMin={ageMin}
        current={shown}
        zone={zone}
        memberName={memberName}
        memberAvatarId={memberAvatarId}
        showChrome={showChrome}
        tabletCentered={tabletCentered}
        predictionPill={predictionPill}
        banner={
          alarmControl ? (
            <>
              {broadcastBanner}
              {alarmControl}
            </>
          ) : (
            broadcastBanner
          )
        }
        tts={tts}
        urgentActive={urgentActive}
        voiceListening={voiceListening}
      >
        {children}
      </MobileFrame>
    );
    if (!tabletCentered) return mobileFrame;
    // Cycle 267.1: center it in a standard-phone-sized box that rotates
    // with the device (the `.phone-box` swaps dims on orientation).
    return (
      <div className="phone-stage-host">
        <div className="phone-box">{mobileFrame}</div>
      </div>
    );
  }

  if (variant === "kiosk") {
    // Cycle 77: side mode replaces the floating bottom pill with the
    // EdgeRail at the left edge of the content region (below the
    // full-width header). The content wrapper drops the
    // `--kiosk-nav-safe-area` bottom padding (no floating pill to clear)
    // and fills the remaining width + full height. Bottom mode is a
    // pixel-identical no-op vs cycle 64/72: the floating FooterTabs pill
    // and `.kioskContent`'s bottom safe-area are unchanged.
    const sideMode = showChrome && navPosition === "side";
    // Cycle 53/199: at scale 1 with the default 600 box (the real Pi) the
    // style is omitted entirely → no containing-block / stacking change →
    // byte-identical. Larger screens (iPad landscape) get the transform
    // only. The Android tablet additionally sets `--kiosk-stage-h` to the
    // 640 box from the SAME `kioskStageHeight()` the scale denominator
    // used, so the `.kiosk` element is exactly 1024×640 before scaling.
    const stageHeight = kioskStageHeight();
    const kioskStyle: CSSProperties = {};
    if (kioskScale !== 1) kioskStyle.transform = `scale(${kioskScale})`;
    if (stageHeight !== KIOSK_STAGE_HEIGHT_DEFAULT) {
      (kioskStyle as Record<string, string>)["--kiosk-stage-h"] =
        `${stageHeight}px`;
    }
    // Cycle 325: fluid width — set the custom property ONLY when the box is
    // wider than the 1024 design floor (a wider-than-design tablet, e.g. the
    // A11). At exactly 1024 it's omitted → the CSS `var(--kiosk-stage-w,
    // 1024px)` default applies → byte-identical to pre-325 (and the Pi at
    // scale 1 still yields an EMPTY style object → no `style` attr at all).
    if (kioskWidth !== KIOSK_STAGE_WIDTH_DEFAULT) {
      (kioskStyle as Record<string, string>)["--kiosk-stage-w"] =
        `${kioskWidth}px`;
    }
    const hasKioskStyle = Object.keys(kioskStyle).length > 0;
    return (
      <div className="kiosk-stage">
        <div
          className={textFocus ? "kiosk kioskTextFocus" : "kiosk"}
          data-overlay-host=""
          style={hasKioskStyle ? kioskStyle : undefined}
        >
          {/* Cycle 84: background-image layer behind all content (renders
              nothing when no background is selected — the theme gradient
              ground shows). */}
          <ThemeBackground />
          {/* Cycle 237: wallpaper scrim — a non-interactive darkening layer
              ABOVE the wallpaper (z-index:-1, after ThemeBackground) and
              BELOW all content/chrome/alarm surfaces, so small/secondary
              text always has a consistent dark substrate. Both nav modes;
              never dims an alarm banner/control (z-index:-1). */}
          <div className="kioskScrim" aria-hidden="true" />
          {header}
          {/* Cycle 97: broadcast banner reserves height in the `.kiosk`
              flex column (it's flex-shrink:0), so the `flex:1` body below
              shrinks and the hero/cards reflow — both nav modes. */}
          {broadcastBanner}
          {/* Cycle 452.2: the compact alarm control no longer sits here under
              the header (with a 96 px dodge around the medallion) — it is
              the bottom banner, in `.kioskContent` below. */}
          {sideMode ? (
            // Side mode: [EdgeRail | content] horizontal row filling the
            // region below the header. No FooterTabs, no bottom safe-area
            // (`.kioskContentSide` drops the `--kiosk-nav-safe-area`
            // padding). The rail reuses its own active-tab/routing/glyph
            // rendering, sized for kiosk touch via `variant="kiosk"`.
            <div className="kioskBody">
              {/* Cycle 103: detached pill rail — reserves the
                  `--kiosk-rail-width` column but paints only a pill in the
                  lower-left (the cycle-79/86 seam is gone). */}
              <EdgeRail unackCount={socket.unackCount} variant="kiosk" />
              {/* Cycle 79.1: no extra content padding — the hero column
                  already centers (vertically + horizontally), so it sits
                  balanced top-to-bottom. */}
              <div
                className={`kioskContent kioskContentSide${noDataBanner ? " kioskContentBottomStrip" : ""}${alarmControl ? " kioskContentAlarmBanner" : ""}`}
                style={kioskContentStyle}
              >
                {/* Cycle 401: FIRST in the DOM, LAST on screen. Cycle 394's
                    rule — the visual move is CSS `order`, never a JSX
                    reorder — so focus order, the `role="alert"` /
                    `aria-live="assertive"` announcement and the cycle-85
                    z-lift are all unchanged; only the position moves.
                    Inside `.kioskContent` so it clears the nav safe-area.
                    Cycle 452.2: the off-Now banner takes the same rule. */}
                {kioskAlarmBanner}
                {noDataBanner}
                {children}
              </div>
            </div>
          ) : (
            <>
              {/* Cycle 64: the content wrapper now spans header-bottom →
                  frame-bottom (no FooterTabs row in flow). The
                  `.kioskContent` class in `tokens.css` paints
                  `padding-bottom: var(--kiosk-nav-safe-area)` so the
                  floating pill nav overlays a same-shaped gap; the
                  measured-chart-fill chain and the numpad/grid screens
                  automatically bottom out above the pill, not behind it. */}
              {/* Cycle 452.1: `kioskContentBottomStrip` marks that the no-data
                  banner is mounted, so a full-stage takeover under it (the
                  tablet's /pair-device) can reserve the band it occupies.
                  No rule reads it anywhere else. */}
              <div
                className={`kioskContent${noDataBanner ? " kioskContentBottomStrip" : ""}${alarmControl ? " kioskContentAlarmBanner" : ""}`}
                style={kioskContentStyle}
              >
                {/* Cycle 401: FIRST in the DOM, LAST on screen — see the
                    side-mode note above and `.kioskBottomStrip`. */}
                {kioskAlarmBanner}
                {noDataBanner}
                {children}
              </div>
              {footer /* cycle 64: position: absolute pill, z-index 40 */}
            </>
          )}
          {/* Cycle 199: the packaged on-screen keyboard is suppressed on
              the Android-tablet kiosk — text focus raises the native
              Android IME instead. The Pi (no OS IME) keeps it. */}
          {showChrome && !isAndroidTabletClassRuntime() ? (
            <OnScreenKeyboard navPosition={navPosition} />
          ) : null}
          {ageDisplayHelpers(ageMin)}
          {/* Cycle 214: the full-width SPEAKING VoiceBanner was removed —
              the Pulse-Core orb beneath the Now hero is now the sole
              speaking indicator + tap-to-stop control. */}
          {/* Cycle 100 — brief "Listening" affirmation on wake.
              Cycle 383 — the SAME banner now also covers a hands-free
              voice command session: when `voiceListening` is true it stays
              up for the whole session (listening + processing) instead of
              the 1.6 s pulse. `active` is only passed when a session is
              actually running, so the cycle-100 wake pulse is untouched. */}
          <WakeBanner
            lastWakeTs={voiceWakeTs}
            variant="kiosk"
            // Gated on chrome so a session banner can never paint over the
            // bedside clock face / login. Passing `undefined` (not false)
            // keeps the cycle-100 wake pulse byte-identical.
            active={showChrome && voiceListening ? true : undefined}
          />
          {/* Cycle 222: transient bottom "speaking" banner (the relocated
              status orb + spoken sentence + Stop). Only with chrome on (never
              over the bedside clock face / login). Cycle 222.1: it spans the
              FULL stage width edge-to-edge (`left: 0` in BOTH nav modes — in
              side-nav it now runs under the left rail's lower edge too); it
              still lifts above the bottom pill in bottom-nav mode. */}
          {showChrome ? (
            <SpeakingBanner
              tts={tts}
              zone={zone}
              urgentActive={urgentActive}
              variant="kiosk"
              /* Cycle 394: the alarm strip now owns the bottom edge of the
                 kiosk Now screen, and this banner is z-45 against the strip's
                 z-12 — so without the lift a voice caption paints straight
                 over Snooze/Stop and `elementFromPoint` stops returning the
                 control. Urgent episodes were already safe (the banner does
                 not render at all), but a plain low/high was NOT: nothing
                 suppresses auto-announce for those. Stack instead of hide, so
                 the caption survives. */
              bottom={
                bottomStripActive
                  ? `calc(${sideMode ? "0px" : "var(--kiosk-nav-safe-area)"} + var(--kiosk-alarm-banner-height, 52px))`
                  : sideMode
                    ? 0
                    : "var(--kiosk-nav-safe-area)"
              }
              left={0}
            />
          ) : null}
        </div>
      </div>
    );
  }

  // Cycle 170: the web frame is the command station — a persistent left
  // nav rail + a live household status strip pinned above a scrolling
  // content canvas that hosts the existing web screens unchanged. The
  // cycle-9 web chrome (EdgeRail + KioskHeader hero) is superseded here;
  // the status strip carries the at-a-glance live readout the header used
  // to, and the rail replaces the EdgeRail. Kiosk + mobile keep their own
  // chrome (the branches above). `header`/`rail`/`footer` are computed
  // above for the kiosk branches and are intentionally unused here.
  return (
    <>
      <WebFrame
        user={user}
        socket={socket}
        now={now}
        units={units}
        members={members}
        activeMemberId={activeMemberId}
        onSelectMember={onSelectMember}
        showChrome={showChrome}
        broadcastBanner={broadcastBanner}
        alarmControl={alarmControl}
        tts={tts}
        zone={zone}
        urgentActive={urgentActive}
      >
        {children}
      </WebFrame>
      {ageDisplayHelpers(ageMin)}
    </>
  );
}

// Return the rendered age helper string for the screen-reader tree only.
// Visible header already shows the formatted age.
function ageDisplayHelpers(ageMin: number) {
  return (
    <span style={{ position: "absolute", left: -9999, top: 0 }} aria-live="polite">
      Last reading {fmtAge(ageMin)}.
    </span>
  );
}
